import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getLoggedInUser } from "../helpers/authHelper";
import { apiRequest } from "../helpers/api";
import { ToastFeedback } from "../components/ToastNotifications";
import { useWorkspaceAccess } from "../components/WorkspaceAccess";
import socialPlatformDetails from "../config/socialPlatforms";
import {
  AnimatedNumber,
  DashboardHeader,
  DashboardSkeleton,
  EmptyState,
  InsightList,
  Panel,
  PanelLink,
  ProportionList,
  QuickActions,
  RefreshButton,
  StatCard,
  formatCompact,
  formatCount,
} from "../components/dashboard/DashboardKit";
import "./Auth.css";
import "./FoundationDashboard.css";

const formatTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Time not set" : date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const formatDay = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const greetingFor = (hour) => hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

const platformInfo = (platform) => socialPlatformDetails[String(platform || "").toLowerCase()] || { name: platform, icon: "ti-world" };

const percent = (part, whole) => whole > 0 ? Math.round((part / whole) * 100) : 0;

const quickActionCatalog = [
  { to: "/ai-post-studio", icon: "ti-sparkles", label: "AI Post Studio", description: "Generate on-brand posts", tone: "teal" },
  { to: "/manual-post-studio", icon: "ti-notes", label: "Manual Post Studio", description: "Write and schedule a post", tone: "blue" },
  { to: "/content-calendar", icon: "ti-calendar-event", label: "Content Calendar", description: "Plan the weeks ahead", tone: "amber" },
  { to: "/messages", icon: "ti-message-circle", label: "Messages & Automations", description: "Reply and automate DMs", tone: "rose" },
  { to: "/media-library", icon: "ti-photo", label: "Media Library", description: "Manage images and video", tone: "green" },
  { to: "/analytics", icon: "ti-chart-bar", label: "Analytics", description: "See what performs best", tone: "coral" },
];

const PostList = ({ posts, includeDate }) => (
  <ul className="sx-timeline">
    {posts.map((post) => {
      const platform = platformInfo(post.platform);
      return <li key={post.id}>
        <span className="sx-time">{formatTime(post.scheduledAt)}{includeDate && <small>{formatDay(post.scheduledAt)}</small>}</span>
        <span className="sx-item-main">
          <span className="sx-platform" aria-hidden="true"><i className={`ti ${platform.icon}`} /></span>
          <span className="sx-item-copy">
            <strong>{post.topic || post.caption || "Scheduled post"}</strong>
            <small>{[platform.name, post.contentType].filter(Boolean).join(" · ")}</small>
          </span>
        </span>
        <span className="sx-badge tone-teal">Scheduled</span>
      </li>;
    })}
  </ul>
);

const buildInsights = ({ posts, inbox, usage, engagementRate }, canOpen) => {
  const insights = [];
  const todayCount = posts.scheduledToday?.length || 0;
  if (inbox.humanHandoffs > 0) insights.push({ id: "handoffs", tone: "critical", icon: "ti-hand-stop", title: `${formatCount(inbox.humanHandoffs)} conversation${inbox.humanHandoffs === 1 ? " needs" : "s need"} a person`, text: "Automation handed these over for a human reply.", to: "/messages", cta: "Open inbox" });
  else if (inbox.unanswered > 0) insights.push({ id: "unanswered", tone: "warning", icon: "ti-message-exclamation", title: `${formatCount(inbox.unanswered)} unanswered message${inbox.unanswered === 1 ? "" : "s"}`, text: "Quick replies keep leads warm.", to: "/messages", cta: "Reply now" });
  if (!todayCount && !posts.scheduled) insights.push({ id: "empty-queue", tone: "warning", icon: "ti-calendar-off", title: "Your publishing queue is empty", text: "Schedule a post to stay visible to your audience.", to: "/ai-post-studio/create", cta: "Generate a post" });
  else if (!todayCount) insights.push({ id: "quiet-day", tone: "info", icon: "ti-calendar-time", title: "Nothing goes out today", text: `${formatCount(posts.scheduled)} post${posts.scheduled === 1 ? " is" : "s are"} queued for later dates.`, to: "/content-calendar", cta: "View calendar" });
  if (posts.drafts > 0) insights.push({ id: "drafts", tone: "info", icon: "ti-file-pencil", title: `${formatCount(posts.drafts)} draft${posts.drafts === 1 ? "" : "s"} waiting`, text: "Review and schedule drafts you have already started.", to: "/manual-post-studio", cta: "Review drafts" });
  if (inbox.leads > 0) insights.push({ id: "leads", tone: "good", icon: "ti-user-star", title: `${formatCount(inbox.leads)} lead${inbox.leads === 1 ? "" : "s"} identified`, text: "Captured automatically from your conversations.", to: "/leads", cta: "See leads" });
  if (engagementRate !== null) insights.push({ id: "engagement", tone: "good", icon: "ti-chart-arrows-vertical", title: `${engagementRate}% engagement rate`, text: "Interactions per impression on posts published this month." });
  if (!usage.content && !usage.images && !usage.videos) insights.push({ id: "ai", tone: "info", icon: "ti-sparkles", title: "Try the AI Post Studio", text: "No AI generations yet this month.", to: "/ai-post-studio", cta: "Start generating" });
  return insights.filter((insight) => !insight.to || canOpen(insight.to)).slice(0, 4);
};

const FoundationDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const { canOpen } = useWorkspaceAccess();
  const user = getLoggedInUser();

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setSummary(await apiRequest("/api/analytics/summary"));
      setUpdatedAt(new Date());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const now = new Date();
  const today = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const month = now.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const posts = summary?.posts || {};
  const usage = summary?.usage || {};
  const inbox = summary?.inbox || {};
  const engagement = summary?.engagement || {};
  const interactions = (engagement.likes || 0) + (engagement.comments || 0) + (engagement.shares || 0);
  const engagementRate = engagement.impressions > 0 ? Math.round((interactions / engagement.impressions) * 1000) / 10 : null;
  const answeredShare = percent((inbox.conversations || 0) - (inbox.unanswered || 0), inbox.conversations || 0);
  const linkIf = (path) => canOpen(path) ? path : undefined;
  const quickActions = quickActionCatalog.filter((action) => canOpen(action.to));
  const insights = summary ? buildInsights({ posts, inbox, usage, engagementRate }, canOpen) : [];
  const status = error ? "Data unavailable" : loading ? "Updating" : updatedAt ? `Updated ${updatedAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : month;

  return (
    <section className="workspace-dashboard sx-dashboard">
      <DashboardHeader
        eyebrow={today}
        title={`${greetingFor(now.getHours())}, ${user?.firstName || "there"}`}
        intro="Your content pipeline, conversations and AI activity for this month, at a glance."
        status={status}
        statusTone={error ? "error" : loading ? "busy" : "live"}
        actions={<>
          <RefreshButton onClick={loadSummary} busy={loading} />
          {canOpen("/ai-post-studio/create") && <Link to="/ai-post-studio/create" className="sx-button is-primary"><i className="ti ti-sparkles" aria-hidden="true" />Create AI post</Link>}
        </>}
      />

      {error && <ToastFeedback type="error" message={error} />}
      {!summary && !loading && error && <EmptyState icon="ti-cloud-off" title="We couldn't load your workspace activity." text={error} action={<button type="button" className="sx-button is-ghost" onClick={loadSummary}><i className="ti ti-refresh" aria-hidden="true" />Try again</button>} />}
      {!summary && loading ? <DashboardSkeleton label="Loading workspace activity..." /> : summary && <>
        <div className="sx-stats">
          <StatCard index={1} icon="ti-calendar-event" label="Scheduled posts" value={posts.scheduled} detail={`${formatCount(posts.scheduledToday?.length)} going out today`} tone="teal" to={linkIf("/content-calendar")} />
          <StatCard index={2} icon="ti-circle-check" label="Published" value={posts.publishedThisMonth} detail="Published this month" tone="green" to={linkIf("/manual-post-studio")} />
          <StatCard index={3} icon="ti-file-text" label="Drafts" value={posts.drafts} detail="Ready for review" tone="coral" to={linkIf("/manual-post-studio")} />
          <StatCard index={4} icon="ti-sparkles" label="AI generations" value={usage.content} detail="Content pieces this month" tone="amber" to={linkIf("/ai-post-studio")} />
          <StatCard index={5} icon="ti-messages" label="Conversations" value={inbox.conversations} detail={`${formatCount(inbox.unanswered)} unanswered`} tone="blue" to={linkIf("/messages")} />
          <StatCard index={6} icon="ti-user-check" label="Leads" value={inbox.leads} detail="Identified from DMs" tone="rose" to={linkIf("/leads")} />
        </div>

        <div className="sx-grid-main">
          <Panel index={7} icon="ti-clock-hour-4" title="Today's schedule" subtitle="Posts queued for today" count={posts.scheduledToday?.length || 0}>
            {posts.scheduledToday?.length
              ? <PostList posts={posts.scheduledToday} />
              : <EmptyState icon="ti-calendar-off" title="No posts scheduled for today." text="Fill the gap with a quick post, or let AI draft one for you." action={canOpen("/manual-post-studio/create") && <Link to="/manual-post-studio/create" className="sx-button is-ghost"><i className="ti ti-plus" aria-hidden="true" />Schedule a post</Link>} />}
          </Panel>
          <Panel index={8} icon="ti-bulb" title="Insights" subtitle="Suggested next steps from your data">
            {insights.length
              ? <InsightList insights={insights} />
              : <EmptyState icon="ti-circle-check" title="You're all caught up" text="Nothing needs your attention right now." />}
          </Panel>
        </div>

        {quickActions.length > 0 && <Panel index={9} icon="ti-bolt" title="Quick actions" subtitle="Jump straight into your most used tools" className="sx-quick-panel">
          <QuickActions actions={quickActions} />
        </Panel>}

        <div className="sx-grid-2">
          <Panel index={10} icon="ti-calendar-week" title="Coming up" subtitle="Your next scheduled posts" action={canOpen("/content-calendar") && <PanelLink to="/content-calendar">Calendar</PanelLink>}>
            {posts.upcoming?.length
              ? <PostList posts={posts.upcoming} includeDate />
              : <EmptyState icon="ti-calendar-plus" title="No upcoming posts scheduled." text="Plan ahead to keep a steady publishing rhythm." />}
          </Panel>
          <Panel index={11} icon="ti-message-chatbot" title="Inbox health" subtitle="Conversations across connected channels" action={canOpen("/messages") && <PanelLink to="/messages">Inbox</PanelLink>}>
            <div className="sx-highlight">
              <div><strong><AnimatedNumber value={answeredShare} suffix="%" /></strong><span>Conversations answered</span></div>
              <div className="sx-meter tone-teal" aria-hidden="true"><span style={{ "--sx-fill": `${answeredShare}%` }} /></div>
            </div>
            <div className="sx-kpis">
              <div className="sx-kpi"><span><i className="ti ti-message-exclamation" aria-hidden="true" />Unanswered</span><strong><AnimatedNumber value={inbox.unanswered} /></strong></div>
              <div className="sx-kpi"><span><i className="ti ti-hand-stop" aria-hidden="true" />Human handoffs</span><strong><AnimatedNumber value={inbox.humanHandoffs} /></strong></div>
              <div className="sx-kpi"><span><i className="ti ti-robot" aria-hidden="true" />Automated replies</span><strong><AnimatedNumber value={inbox.automatedReplies} /></strong></div>
            </div>
          </Panel>
        </div>

        <div className="sx-grid-2">
          <Panel index={12} icon="ti-sparkles" title="AI usage" subtitle="Generation activity this month">
            <ProportionList items={[
              { key: "content", label: "Content generation", icon: "ti-writing", value: usage.content, tone: "teal" },
              { key: "images", label: "AI images", icon: "ti-photo", value: usage.images, tone: "blue" },
              { key: "videos", label: "AI videos", icon: "ti-video", value: usage.videos, tone: "amber" },
            ]} />
            <p className="sx-note">Usage tracking starts when a generation completes.</p>
          </Panel>
          <Panel index={13} icon="ti-chart-bar" title="Engagement" subtitle="On posts published this month" action={canOpen("/analytics") && <PanelLink to="/analytics">Analytics</PanelLink>}>
            <div className="sx-kpis">
              {[["Likes", "ti-heart", engagement.likes], ["Comments", "ti-message", engagement.comments], ["Shares", "ti-share", engagement.shares], ["Reach", "ti-users", engagement.reach], ["Impressions", "ti-eye", engagement.impressions]].map(([label, icon, value]) => (
                <div className="sx-kpi" key={label}><span><i className={`ti ${icon}`} aria-hidden="true" />{label}</span><strong><AnimatedNumber value={value} format={formatCompact} /></strong></div>
              ))}
              <div className="sx-kpi"><span><i className="ti ti-percentage" aria-hidden="true" />Engagement rate</span><strong>{engagementRate === null ? "—" : `${engagementRate}%`}</strong></div>
            </div>
          </Panel>
        </div>
      </>}
    </section>
  );
};

export default FoundationDashboard;
